/**
 * @fileoverview Department Documents tab for viewing and managing fee schedule and ordinance uploads.
 * @module system-web/pages/DepartmentDocumentsTab
 *
 * Responsibilities:
 * - Display uploaded fee schedules and ordinances for a department.
 * - Allow system admins to upload new fee schedule documents.
 * - Show AI-parsed fee rule extraction status and results.
 *
 * Dependencies:
 * - apiFetch and apiPostFormData from system-web apiClient
 *
 * Architecture:
 * - Online-only admin operation. Uses system-api endpoints directly since this is
 *   a cross-tenant admin view (system admins viewing/managing tenant documents).
 *   Local-first Dexie is not used here because system-web doesn't have the tenant Dexie DB.
 *
 * Notes:
 * - Fee schedule parsing is asynchronous (background AI job). The UI polls or refreshes manually.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, Upload, RefreshCw, Loader2, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle2, Clock, Info, X,
} from 'lucide-react';
import { apiPostFormData } from '../apiClient';
import { listFeeScheduleDocuments, getFeeScheduleDocument } from '../services/feeScheduleService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract FeeScheduleStatus
 *
 * Purpose:
 * - Represents the AI-parsing lifecycle state for an uploaded fee schedule document.
 *
 * Fields:
 * - pending:    Document uploaded; parse job not yet started.
 * - parsing:    Background AI job is actively extracting fee rules.
 * - active:     Parsing complete; rules are available.
 * - superseded: A newer document has replaced this one.
 * - error:      Parse job failed; parseError holds the reason.
 */
type FeeScheduleStatus = 'pending' | 'parsing' | 'active' | 'superseded' | 'error';

type DocumentType = 'fee_schedule' | 'ordinance' | 'other';

interface FeeRule {
  _id?: string;
  category?: string;
  subcategory?: string;
  description: string;
  basis?: string;
  amount?: number;
  rate?: number;
  minFee?: number;
  maxFee?: number;
  codeReference?: string;
}

interface FeeScheduleDoc {
  _id: string;
  documentType: DocumentType;
  originalFileName: string;
  status: FeeScheduleStatus;
  parseError?: string;
  ruleCount?: number;
  effectiveDate?: string;
  uploadedAt: string;
  rules?: FeeRule[];
}

interface UploadFormState {
  documentType: DocumentType;
  effectiveDate: string;
  file: File | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  fee_schedule: 'Fee Schedule',
  ordinance: 'Ordinance',
  other: 'Other',
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt'];

/**
 * @component StatusBadge
 *
 * Purpose:
 * - Render a coloured pill badge for a fee schedule document's parse status.
 *
 * Props:
 * - status: Current parse lifecycle status of the document.
 */
function StatusBadge({ status }: { status: FeeScheduleStatus }) {
  if (status === 'parsing') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Loader2 className="h-3 w-3 animate-spin" /> Parsing
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 className="h-3 w-3" /> Active
      </span>
    );
  }
  if (status === 'superseded') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        Superseded
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <AlertCircle className="h-3 w-3" /> Error
      </span>
    );
  }
  // pending
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
}

// ---------------------------------------------------------------------------
// UploadModal
// ---------------------------------------------------------------------------

interface UploadModalProps {
  tenantId: string;
  theme: any;
  onClose: () => void;
  onUploaded: (doc: FeeScheduleDoc) => void;
}

/**
 * @component UploadModal
 *
 * Purpose:
 * - Modal form for uploading a new fee schedule or ordinance document.
 *
 * Props:
 * - tenantId:   Target department tenant ID.
 * - theme:      Visual theme object.
 * - onClose:    Called when the modal should be dismissed.
 * - onUploaded: Called with the created document record after a successful upload.
 *
 * Behavior:
 * - POSTs multipart form data to `/api/tenants/:id/fee-schedules/upload`.
 * - Validates file type (.pdf, .docx, .txt) and size (max 50 MB) client-side.
 */
function UploadModal({ tenantId, theme, onClose, onUploaded }: UploadModalProps) {
  const [form, setForm] = useState<UploadFormState>({
    documentType: 'fee_schedule',
    effectiveDate: '',
    file: null,
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setForm(f => ({ ...f, file: null }));
      return;
    }
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Only ${ALLOWED_EXTENSIONS.join(', ')} files are supported.`);
      setForm(f => ({ ...f, file: null }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File must be 50 MB or smaller.');
      setForm(f => ({ ...f, file: null }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setError(null);
    setForm(f => ({ ...f, file }));
  }

  async function handleUpload() {
    if (!form.file) {
      setError('Please select a file.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('documentType', form.documentType);
      if (form.effectiveDate) fd.append('effectiveDate', form.effectiveDate);
      const result = await apiPostFormData<{ document: FeeScheduleDoc }>(
        `/api/tenants/${tenantId}/fee-schedules/upload`,
        fd,
      );
      onUploaded(result.document);
    } catch (err: any) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        style={{ background: theme.cardBg }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: theme.cardBorder, background: theme.expandedBg }}
        >
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4" style={{ color: theme.accent }} />
            <span className="text-sm font-semibold" style={{ color: theme.textPrimary }}>
              Upload Document
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {/* Document Type */}
          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: theme.textSecondary }}
            >
              Document Type
            </label>
            <select
              value={form.documentType}
              onChange={e => setForm(f => ({ ...f, documentType: e.target.value as DocumentType }))}
              className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
              style={{
                background: theme.cardBg,
                color: theme.textPrimary,
                borderColor: theme.cardBorder,
              }}
            >
              <option value="fee_schedule">Fee Schedule</option>
              <option value="ordinance">Ordinance</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Effective Date */}
          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: theme.textSecondary }}
            >
              Effective Date{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="date"
              value={form.effectiveDate}
              onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
              style={{
                background: theme.cardBg,
                color: theme.textPrimary,
                borderColor: theme.cardBorder,
              }}
            />
          </div>

          {/* File */}
          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: theme.textSecondary }}
            >
              File{' '}
              <span className="font-normal text-gray-400">(PDF, DOCX, TXT — max 50 MB)</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
            />
            {form.file && (
              <p className="mt-1 text-xs" style={{ color: theme.textSecondary }}>
                {form.file.name} ({(form.file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-1.5 text-xs text-red-600">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-4 py-3 border-t"
          style={{ borderColor: theme.cardBorder, background: theme.expandedBg }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50"
            style={{ borderColor: theme.cardBorder, color: theme.textSecondary }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!form.file || uploading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ background: theme.accent, color: theme.accentContrast ?? '#ffffff' }}
          >
            {uploading
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Upload className="h-3.5 w-3.5" />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeeRulesTable
// ---------------------------------------------------------------------------

/**
 * @component FeeRulesTable
 *
 * Purpose:
 * - Render a table of AI-extracted fee rules for an active fee schedule document.
 *
 * Props:
 * - rules: Extracted fee rule records from the parsed document.
 * - theme: Visual theme object.
 */
function FeeRulesTable({ rules, theme }: { rules: FeeRule[]; theme: any }) {
  if (rules.length === 0) {
    return (
      <p
        className="text-xs text-center py-4"
        style={{ color: theme.textMuted ?? theme.textSecondary }}
      >
        No fee rules extracted.
      </p>
    );
  }

  const columns = [
    'Category',
    'Subcategory',
    'Description',
    'Basis',
    'Amount / Rate',
    'Min Fee',
    'Max Fee',
    'Code Ref',
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
            {columns.map(col => (
              <th
                key={col}
                className="px-2 py-1.5 text-left font-medium whitespace-nowrap"
                style={{ color: theme.textSecondary }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rules.map((rule, i) => (
            <tr
              key={rule._id ?? i}
              style={{ borderBottom: `1px solid ${theme.expandedCardBorder}` }}
            >
              <td className="px-2 py-1.5" style={{ color: theme.textPrimary }}>
                {rule.category ?? '—'}
              </td>
              <td className="px-2 py-1.5" style={{ color: theme.textPrimary }}>
                {rule.subcategory ?? '—'}
              </td>
              <td
                className="px-2 py-1.5 max-w-[200px] truncate"
                style={{ color: theme.textPrimary }}
                title={rule.description}
              >
                {rule.description}
              </td>
              <td className="px-2 py-1.5" style={{ color: theme.textSecondary }}>
                {rule.basis ?? '—'}
              </td>
              <td className="px-2 py-1.5 font-mono" style={{ color: theme.textPrimary }}>
                {rule.amount != null
                  ? `$${rule.amount.toLocaleString()}`
                  : rule.rate != null
                  ? `$${rule.rate}/unit`
                  : '—'}
              </td>
              <td className="px-2 py-1.5 font-mono" style={{ color: theme.textSecondary }}>
                {rule.minFee != null ? `$${rule.minFee.toLocaleString()}` : '—'}
              </td>
              <td className="px-2 py-1.5 font-mono" style={{ color: theme.textSecondary }}>
                {rule.maxFee != null ? `$${rule.maxFee.toLocaleString()}` : '—'}
              </td>
              <td
                className="px-2 py-1.5 font-mono text-[10px]"
                style={{ color: theme.textSecondary }}
              >
                {rule.codeReference ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DepartmentDocumentsTab
// ---------------------------------------------------------------------------

/**
 * @component DepartmentDocumentsTab
 *
 * Purpose:
 * - Admin tab for viewing and uploading fee schedule / ordinance documents for a department.
 *
 * Props:
 * - tenant: Department record (requires `_id`).
 * - theme:  Visual theme from ThemeContext.
 *
 * Behavior:
 * - Loads fee schedule documents from `GET /api/tenants/:id/fee-schedules` on mount.
 * - Allows uploading new documents via `POST /api/tenants/:id/fee-schedules/upload`.
 * - Fetches fee rules lazily from `GET /api/tenants/:id/fee-schedules/:docId` when
 *   a row is expanded for the first time.
 * - Displays parse status badges and an expandable fee rule table per document.
 *
 * Data Source:
 * - online — system-api cross-tenant admin endpoints.
 *
 * Constraints:
 * - Requires an active internet connection. Not available offline.
 */
export function DepartmentDocumentsTab({ tenant, theme }: { tenant: any; theme: any }) {
  const [docs, setDocs] = useState<FeeScheduleDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rulesLoadingId, setRulesLoadingId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const loadDocs = useCallback(async () => {
    if (!tenant?._id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listFeeScheduleDocuments(tenant._id);
      // Support both { documents: [...] } and plain array responses.
      setDocs(Array.isArray(result) ? result : (result.documents ?? []));
    } catch (err: any) {
      setError(err.message || 'Failed to load fee schedule documents.');
    } finally {
      setLoading(false);
    }
  }, [tenant?._id]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  async function handleExpandRow(doc: FeeScheduleDoc) {
    if (expandedId === doc._id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(doc._id);

    // Lazily fetch rules for active documents that haven't been loaded yet.
    if (doc.status === 'active' && !doc.rules) {
      setRulesLoadingId(doc._id);
      try {
        const result = await getFeeScheduleDocument(tenant._id, doc._id);
        const rules: FeeRule[] =
          result.rules ?? result.document?.rules ?? [];
        setDocs(prev =>
          prev.map(d => (d._id === doc._id ? { ...d, rules } : d)),
        );
      } catch {
        // Leave rules as undefined; the table will render the empty-state message.
      } finally {
        setRulesLoadingId(null);
      }
    }
  }

  function handleUploaded(doc: FeeScheduleDoc) {
    setDocs(prev => [doc, ...prev]);
    setShowUpload(false);
  }

  return (
    <div className="space-y-4">
      {/* ── Header row ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" style={{ color: theme.accent }} />
          <span className="text-sm font-semibold" style={{ color: theme.textPrimary }}>
            Fee Schedules &amp; Ordinances
          </span>
          {docs.length > 0 && (
            <span
              className="px-1.5 py-0.5 text-[10px] font-medium rounded-full"
              style={{ background: theme.expandedBg, color: theme.textSecondary }}
            >
              {docs.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={loadDocs}
            disabled={loading}
            title="Refresh"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50"
            style={{ borderColor: theme.cardBorder, color: theme.textSecondary }}
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors"
            style={{ background: theme.accent, color: theme.accentContrast ?? '#ffffff' }}
          >
            <Upload className="h-3 w-3" />
            Upload Document
          </button>
        </div>
      </div>

      {/* ── Info banner ── */}
      <div
        className="flex items-start gap-2 rounded-lg p-3"
        style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
      >
        <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700">
          Upload fee schedules and ordinances for this department. The AI parser will extract
          individual fee rules automatically. Parsing runs in the background — refresh to check
          for updates.
        </p>
      </div>

      {/* ── Loading state ── */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: theme.accent }} />
        </div>
      )}

      {/* ── Error state ── */}
      {!loading && error && (
        <div
          className="flex items-start gap-2 rounded-lg p-3"
          style={{ background: '#fef2f2', border: '1px solid #fecaca' }}
        >
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-700">
            <p className="font-medium">Failed to load documents</p>
            <p className="text-xs mt-0.5 opacity-75">{error}</p>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && !error && docs.length === 0 && (
        <div
          className="rounded-lg border-2 border-dashed flex flex-col items-center justify-center py-12 space-y-3"
          style={{ borderColor: theme.cardBorder }}
        >
          <FileText
            className="h-10 w-10"
            style={{ color: theme.textMuted ?? theme.textSecondary }}
          />
          <div className="text-center">
            <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>
              No documents uploaded
            </p>
            <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>
              Upload fee schedules or ordinances to enable AI-powered rule extraction.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors"
            style={{ background: theme.accent, color: theme.accentContrast ?? '#ffffff' }}
          >
            <Upload className="h-3.5 w-3.5" />
            Upload Document
          </button>
        </div>
      )}

      {/* ── Document rows ── */}
      {!loading && !error && docs.length > 0 && (
        <div className="space-y-2">
          {docs.map(doc => {
            const isExpanded = expandedId === doc._id;
            const isRulesLoading = rulesLoadingId === doc._id;

            return (
              <div
                key={doc._id}
                className="rounded-lg overflow-hidden"
                style={{ border: `1px solid ${theme.expandedCardBorder}` }}
              >
                {/* Row header (clickable) */}
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors"
                  style={{ background: theme.cardBg }}
                  onClick={() => handleExpandRow(doc)}
                >
                  {isExpanded
                    ? <ChevronDown
                        className="h-3.5 w-3.5 flex-shrink-0"
                        style={{ color: theme.textMuted ?? theme.textSecondary }}
                      />
                    : <ChevronRight
                        className="h-3.5 w-3.5 flex-shrink-0"
                        style={{ color: theme.textMuted ?? theme.textSecondary }}
                      />}
                  <FileText
                    className="h-3.5 w-3.5 flex-shrink-0"
                    style={{ color: theme.textSecondary }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-sm font-medium truncate"
                        style={{ color: theme.textPrimary }}
                      >
                        {doc.originalFileName}
                      </span>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                        style={{
                          background: theme.expandedBg,
                          color: theme.textSecondary,
                        }}
                      >
                        {DOCUMENT_TYPE_LABELS[doc.documentType] ?? doc.documentType}
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-3 mt-0.5 text-[11px]"
                      style={{ color: theme.textSecondary }}
                    >
                      {doc.effectiveDate && (
                        <span>
                          Effective {new Date(doc.effectiveDate).toLocaleDateString()}
                        </span>
                      )}
                      <span>
                        Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {doc.ruleCount != null && doc.status === 'active' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                        {doc.ruleCount} rules
                      </span>
                    )}
                    <StatusBadge status={doc.status} />
                  </div>
                </button>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div
                    className="border-t px-3 py-3 space-y-2"
                    style={{
                      borderColor: theme.expandedCardBorder,
                      background: theme.expandedBg,
                    }}
                  >
                    {/* Parse error message */}
                    {doc.status === 'error' && doc.parseError && (
                      <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
                        <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Parse error</p>
                          <p className="mt-0.5 opacity-75">{doc.parseError}</p>
                        </div>
                      </div>
                    )}

                    {/* Parsing / pending in-progress notice */}
                    {(doc.status === 'pending' || doc.status === 'parsing') && (
                      <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                        <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                        <span>
                          AI parsing is in progress. Use <strong>Refresh</strong> to check for updates.
                        </span>
                      </div>
                    )}

                    {/* Rules loading spinner */}
                    {isRulesLoading && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2
                          className="h-4 w-4 animate-spin"
                          style={{ color: theme.accent }}
                        />
                      </div>
                    )}

                    {/* Fee rules table */}
                    {!isRulesLoading && doc.status === 'active' && (
                      <div
                        className="rounded-lg overflow-hidden"
                        style={{ border: `1px solid ${theme.expandedCardBorder}` }}
                      >
                        <div
                          className="px-3 py-2 flex items-center gap-1.5"
                          style={{ background: theme.cardBg }}
                        >
                          <span
                            className="text-xs font-semibold"
                            style={{ color: theme.textPrimary }}
                          >
                            Extracted Fee Rules
                          </span>
                          {doc.rules && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                              style={{
                                background: theme.expandedBg,
                                color: theme.textSecondary,
                              }}
                            >
                              {doc.rules.length}
                            </span>
                          )}
                        </div>
                        <FeeRulesTable rules={doc.rules ?? []} theme={theme} />
                      </div>
                    )}

                    {/* Superseded notice */}
                    {doc.status === 'superseded' && (
                      <p className="text-xs" style={{ color: theme.textSecondary }}>
                        This document has been superseded by a newer upload.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Upload modal ── */}
      {showUpload && (
        <UploadModal
          tenantId={tenant._id}
          theme={theme}
          onClose={() => setShowUpload(false)}
          onUploaded={handleUploaded}
        />
      )}
    </div>
  );
}

export default DepartmentDocumentsTab;
